import { useEffect, useMemo, useState, useCallback } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import PreloadedTrucksImporterCard from "../components/admin/PreloadedTrucksImporterCard";
import {
  Buildings, CaretLeft, Plus, PencilSimple, FloppyDisk, X, Globe, Lock, MapPin, Note, Sparkle,
} from "@phosphor-icons/react";

/**
 * /admin/preloaded-trucks — dedicated admin workspace for managing
 * preloaded truck profiles ahead of city launches.
 *
 * REGIONAL_PARITY_EXEMPT — this file is one of exactly THREE places
 * in the codebase where "preloaded" state is legitimately referenced.
 * The other two are `backend/server.py::_autocreate_profile_draft_from_preload`
 * (claim persistence) and `frontend/src/pages/PreloadedTruckProfile.jsx`
 * (public claim landing). All owner-facing surfaces treat claimed
 * trucks as normal TruckTrackr trucks. See `/app/memory/PRD.md` →
 * Core product principle → Regional Parity.
 *
 * Layout is intentionally section-based with generous whitespace so we
 * can append future surfaces (Import History, Claim Requests, Activity
 * Log) without redesigning the page:
 *
 *   1. City selector & metadata
 *   2. City Progress Dashboard (provided by the importer card)
 *   3. CSV Import (Preview → Confirm)
 *   4. Truck Management (table)
 *   [ Future sections drop in below — placeholder marker visible. ]
 *
 * City state is persisted to `?city=…` so refresh/share keeps the
 * admin where they were and we don't need a global zustand store.
 */
export default function AdminPreloadedTrucks() {
  const { user, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(true);
  const [activeCitySlug, setActiveCitySlug] = useState("austin");
  const [showAddCity, setShowAddCity] = useState(false);
  const [editingCity, setEditingCity] = useState(null);

  const loadCities = useCallback(async () => {
    setLoadingCities(true);
    try {
      const { data } = await api.get("/admin/expansion-cities");
      setCities(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load cities");
    } finally {
      setLoadingCities(false);
    }
  }, []);

  useEffect(() => { loadCities(); }, [loadCities]);

  // Restore the city from the URL on first paint (and on browser back/forward).
  useEffect(() => {
    const urlCity = (searchParams.get("city") || "").toLowerCase().trim();
    if (urlCity && urlCity !== activeCitySlug) {
      setActiveCitySlug(urlCity);
    } else if (!urlCity) {
      // Default to austin and stamp the URL so reload survives.
      setSearchParams({ city: activeCitySlug }, { replace: true });
    }
  }, [searchParams, activeCitySlug, setSearchParams]);

  const activeCity = useMemo(
    () => cities.find((c) => c.slug === activeCitySlug),
    [cities, activeCitySlug],
  );

  // Auth gate (kept after hook calls so React's rules-of-hooks is happy).
  // Wait for authLoading so we don't bounce the user to /login on the
  // brief moment between session restore and `user` being populated.
  if (authLoading) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: "/admin/preloaded-trucks" }} />;
  if (!user.is_admin) return <Navigate to="/" replace />;

  const handleCityChange = (slug) => {
    if (slug === "__custom__") {
      setShowAddCity(true);
      return;
    }
    setActiveCitySlug(slug);
    setSearchParams({ city: slug }, { replace: false });
  };

  const handleAddCity = async (payload) => {
    try {
      const { data } = await api.post("/admin/expansion-cities", payload);
      toast.success(`Added ${data.name}`);
      setShowAddCity(false);
      await loadCities();
      setActiveCitySlug(data.slug);
      setSearchParams({ city: data.slug }, { replace: false });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't add city");
    }
  };

  const handleUpdateCity = async (slug, patch) => {
    try {
      await api.put(`/admin/expansion-cities/${slug}`, patch);
      toast.success("Saved");
      setEditingCity(null);
      await loadCities();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save city");
    }
  };

  const handleDeleteCity = async (slug) => {
    if (!window.confirm(`Delete the "${slug}" city entry? This won't touch any preloaded trucks already in the DB.`)) return;
    try {
      await api.delete(`/admin/expansion-cities/${slug}`);
      toast.success(`Deleted ${slug}`);
      setEditingCity(null);
      // If the user deleted the currently-active city, fall back to
      // austin and clear the stale ?city= URL param so a reload doesn't
      // try to load the now-missing slug.
      if (activeCitySlug === slug) {
        setActiveCitySlug("austin");
        setSearchParams({ city: "austin" }, { replace: true });
      }
      await loadCities();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't delete city");
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-5 max-w-6xl">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3"
            data-testid="preloaded-back-link"
          >
            <CaretLeft weight="bold" className="h-3.5 w-3.5" />
            Back to Admin
          </Link>
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-primary mb-1">
                <Buildings weight="duotone" className="h-3.5 w-3.5" />
                Admin · expansion engine
              </div>
              <h1 className="text-3xl font-bold tracking-tight">Preloaded Trucks</h1>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                Pre-stage truck profiles for upcoming cities. Upload via CSV, review and edit, then
                send claim invites to owners — all before the public market goes live.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-6xl space-y-10">
        {/* Section 1 — City selector & metadata */}
        <PageSection
          number="01"
          title="City"
          subtitle="Pick the market you're working on. Use 'Custom city…' to start researching anywhere not in the dropdown."
        >
          <div className="flex flex-wrap items-end gap-3" data-testid="preloaded-city-bar">
            <div className="min-w-[280px]">
              <Label className="text-xs">Active city</Label>
              <Select value={activeCitySlug} onValueChange={handleCityChange} disabled={loadingCities}>
                <SelectTrigger data-testid="preloaded-city-select" className="h-11">
                  <SelectValue placeholder="Choose a city…" />
                </SelectTrigger>
                <SelectContent>
                  {cities.map((c) => (
                    <SelectItem key={c.slug} value={c.slug} data-testid={`city-option-${c.slug}`}>
                      <span className="inline-flex items-center gap-2">
                        <span className="font-medium">{c.name}{c.state ? `, ${c.state}` : ""}</span>
                        {c.public_status === "live" ? (
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">
                            Live
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                            Coming soon
                          </span>
                        )}
                        {!c.is_default_planned && (
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                            Custom
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                  <div className="border-t border-border my-1" />
                  <SelectItem value="__custom__" data-testid="city-option-custom">
                    <span className="inline-flex items-center gap-1 text-primary font-semibold">
                      <Plus weight="bold" className="h-3.5 w-3.5" />
                      Custom city…
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {activeCity && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setEditingCity(activeCity)}
                className="h-11"
                data-testid="preloaded-edit-city-btn"
              >
                <PencilSimple weight="bold" className="h-4 w-4 mr-1.5" />
                Edit metadata
              </Button>
            )}
          </div>

          {activeCity ? (
            <CityMetaCard city={activeCity} />
          ) : !loadingCities ? (
            <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
              No metadata found for &quot;{activeCitySlug}&quot;. Pick a city from the dropdown or add a custom one.
            </div>
          ) : null}
        </PageSection>

        {/* Sections 2-4 — provided by the importer card. The card already
            internally renders: City Progress Dashboard → CSV Import →
            Truck Management. By passing `cityOverride` we put the page
            in charge of the city; the card hides its own input. */}
        <PageSection
          number="02"
          title="City Progress · CSV Import · Truck Management"
          subtitle="Dashboard refreshes from the selected city. Upload a CSV, preview, confirm, then manage the rows below."
        >
          <PreloadedTrucksImporterCard
            cityOverride={activeCitySlug}
            highlightSlug={(searchParams.get("highlight") || "").trim() || null}
          />
        </PageSection>

        {/* Founding Pro slot roster — visibility + one-click release for
            trial claims that accidentally consumed slots. Only renders
            when the active city actually has a promo configured. */}
        {(activeCity?.launch_promo_slots_total || 0) > 0 && (
          <PageSection
            number="03"
            title={`${activeCity?.launch_promo_label || "Founding Pro"} slot roster`}
            subtitle="Who's holding each promo slot right now, and a one-click release for trial claims or owners who backed out."
          >
            <FoundingSlotsPanel
              key={activeCitySlug}
              citySlug={activeCitySlug}
              cityLabel={activeCity?.name || activeCitySlug}
              onReleased={loadCities}
            />
          </PageSection>
        )}

        {/* Reserved space for future surfaces — visible placeholder so
            this page communicates "more is coming" without being empty. */}
        <PageSection
          number="04"
          title="Coming soon"
          subtitle="Future sections will live here without redesigning the page."
          muted
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="preloaded-future-sections">
            <FutureTile label="Import History" hint="Replay or roll back any past CSV upload." />
            <FutureTile label="Claim Requests" hint="Inbox for owners requesting verification." />
            <FutureTile label="Activity Log" hint="Audit trail of every row edit, invite, archive." />
          </div>
        </PageSection>
      </div>

      {/* City metadata modals */}
      {showAddCity && (
        <CityModal
          mode="create"
          onClose={() => setShowAddCity(false)}
          onSubmit={handleAddCity}
        />
      )}
      {editingCity && (
        <CityModal
          mode="edit"
          city={editingCity}
          onClose={() => setEditingCity(null)}
          onSubmit={(patch) => handleUpdateCity(editingCity.slug, patch)}
          onDelete={() => handleDeleteCity(editingCity.slug)}
        />
      )}
    </div>
  );
}

function PageSection({ number, title, subtitle, children, muted }) {
  return (
    <section className={`space-y-4 ${muted ? "opacity-70" : ""}`} data-testid={`section-${number}`}>
      <div className="flex items-baseline gap-3">
        <span className="text-xs font-mono text-muted-foreground">{number}</span>
        <div>
          <h2 className="text-lg font-bold tracking-tight">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">{subtitle}</p>}
        </div>
      </div>
      <div className="pl-0 sm:pl-7">{children}</div>
    </section>
  );
}

function CityMetaCard({ city }) {
  return (
    <div className="rounded-xl border border-border bg-muted/20 p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs" data-testid="city-meta-card">
      <Meta label="State" icon={MapPin}>{city.state || "—"}</Meta>
      <Meta label="Public status" icon={city.public_status === "live" ? Globe : Lock}>
        {city.public_status === "live" ? (
          <span className="font-bold text-emerald-700">Live</span>
        ) : (
          <span className="font-bold text-amber-700">Coming soon</span>
        )}
      </Meta>
      <Meta label="Landing page" icon={Globe}>
        {city.landing_page_url ? (
          <a
            href={city.landing_page_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline break-all"
            data-testid="city-meta-landing-link"
          >
            {city.landing_page_url}
          </a>
        ) : "—"}
      </Meta>
      <Meta label="Slug" icon={Lock}>
        <code className="font-mono text-foreground">{city.slug}</code>
      </Meta>
      {/* Founding-Pro launch promo section. We always render this so
          admins can see at a glance whether a city has a promo active
          and how many slots are left. */}
      {(city.launch_promo_slots_total || 0) > 0 && (
        <div className="sm:col-span-2 lg:col-span-4 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 p-3" data-testid="city-promo-section">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 inline-flex items-center gap-1 mb-2">
            <Sparkle weight="duotone" className="h-3 w-3" /> {city.launch_promo_label || "Founding Pro"} promo
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <PromoStat label="Total" value={city.launch_promo_slots_total} testId="promo-total" />
            <PromoStat label="Granted" value={city.launch_promo_slots_granted || 0} accent="emerald" testId="promo-granted" />
            <PromoStat label="Reserved" value={city.launch_promo_slots_reserved || 0} accent="amber" testId="promo-reserved" />
            <PromoStat label="Available" value={city.launch_promo_slots_available} accent={city.launch_promo_active ? "primary" : "muted"} testId="promo-available" />
          </div>
          {!city.launch_promo_active && (
            <div className="mt-2 text-[11px] text-muted-foreground italic">
              Promo exhausted — all slots reserved or granted. Increase total in &quot;Edit metadata&quot; to add more.
            </div>
          )}
        </div>
      )}
      {city.admin_notes && (
        <div className="sm:col-span-2 lg:col-span-4 rounded-lg bg-card border border-border p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1 mb-1">
            <Note weight="duotone" className="h-3 w-3" /> Admin notes
          </div>
          <div className="text-xs text-foreground whitespace-pre-wrap">{city.admin_notes}</div>
        </div>
      )}
    </div>
  );
}

function Meta({ label, icon: Icon, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1 mb-1">
        {Icon && <Icon weight="duotone" className="h-3 w-3" />} {label}
      </div>
      <div className="text-xs">{children}</div>
    </div>
  );
}

function PromoStat({ label, value, accent, testId }) {
  const tone = {
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    primary: "text-primary",
    muted: "text-muted-foreground",
  }[accent] || "text-foreground";
  return (
    <div className="bg-white/60 rounded-md px-2 py-1.5" data-testid={testId}>
      <div className={`text-2xl font-bold ${tone}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
    </div>
  );
}

function FutureTile({ label, hint }) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-5 text-center">
      <div className="text-xs font-bold text-foreground">{label}</div>
      <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>
    </div>
  );
}

function CityModal({ mode, city, onClose, onSubmit, onDelete }) {
  const [form, setForm] = useState({
    slug: city?.slug || "",
    name: city?.name || "",
    state: city?.state || "",
    public_status: city?.public_status || "coming_soon",
    landing_page_url: city?.landing_page_url || "",
    admin_notes: city?.admin_notes || "",
    launch_promo_slots_total: city?.launch_promo_slots_total ?? "",
    launch_promo_label: city?.launch_promo_label || "",
  });
  const [saving, setSaving] = useState(false);

  const isCreate = mode === "create";
  const isDefault = !!city?.is_default_planned;

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const promoTotal = form.launch_promo_slots_total === ""
        ? null
        : Math.max(0, parseInt(form.launch_promo_slots_total, 10) || 0);
      const promoLabel = (form.launch_promo_label || "").trim() || null;
      if (isCreate) {
        await onSubmit({
          slug: form.slug,
          name: form.name,
          state: form.state || null,
          public_status: form.public_status,
          landing_page_url: form.landing_page_url || null,
          admin_notes: form.admin_notes || null,
          launch_promo_slots_total: promoTotal,
          launch_promo_label: promoLabel,
        });
      } else {
        await onSubmit({
          name: form.name,
          state: form.state || null,
          public_status: form.public_status,
          landing_page_url: form.landing_page_url || null,
          admin_notes: form.admin_notes || null,
          launch_promo_slots_total: promoTotal,
          launch_promo_label: promoLabel,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="city-modal">
      <div
        className="bg-background rounded-lg max-w-lg w-full p-6 space-y-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              {isCreate ? "Add a custom city" : "Edit city metadata"}
            </div>
            <h3 className="text-lg font-bold mt-1">{isCreate ? "New market" : form.name || form.slug}</h3>
          </div>
          <Button type="button" variant="ghost" size="icon" onClick={onClose} data-testid="city-modal-close">
            <X weight="bold" className="h-5 w-5" />
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Slug</Label>
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().trim() })}
              placeholder="charlotte"
              disabled={!isCreate}
              data-testid="city-modal-slug"
            />
            <p className="text-[10px] text-muted-foreground mt-1">URL-safe. Cannot be changed after creation.</p>
          </div>
          <div>
            <Label className="text-xs">City name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Charlotte"
              data-testid="city-modal-name"
            />
          </div>
          <div>
            <Label className="text-xs">State</Label>
            <Input
              value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value })}
              placeholder="NC"
              maxLength={4}
              data-testid="city-modal-state"
            />
          </div>
          <div>
            <Label className="text-xs">Public status</Label>
            <select
              value={form.public_status}
              onChange={(e) => setForm({ ...form, public_status: e.target.value })}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm w-full"
              data-testid="city-modal-status"
            >
              <option value="coming_soon">Coming soon</option>
              <option value="live">Live</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Public landing page URL</Label>
            <Input
              value={form.landing_page_url}
              onChange={(e) => setForm({ ...form, landing_page_url: e.target.value })}
              placeholder="/food-trucks/charlotte"
              data-testid="city-modal-landing"
            />
          </div>
          <div>
            <Label className="text-xs">Founding-Pro slots</Label>
            <Input
              type="number"
              min="0"
              value={form.launch_promo_slots_total}
              onChange={(e) => setForm({ ...form, launch_promo_slots_total: e.target.value })}
              placeholder="5"
              data-testid="city-modal-promo-slots"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              {city && (city.launch_promo_slots_granted || 0) + (city.launch_promo_slots_reserved || 0) > 0
                ? `${(city.launch_promo_slots_granted || 0) + (city.launch_promo_slots_reserved || 0)} already used. Cannot lower below that.`
                : "Total seats. First N verified claims get permanent free Pro + a slot number."}
            </p>
          </div>
          <div>
            <Label className="text-xs">Promo label</Label>
            <Input
              value={form.launch_promo_label}
              onChange={(e) => setForm({ ...form, launch_promo_label: e.target.value })}
              placeholder="Austin Founding Pro"
              data-testid="city-modal-promo-label"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Used on profile badges: &quot;{form.launch_promo_label || "Founding Pro"} #1&quot;.
            </p>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Admin notes</Label>
            <Textarea
              value={form.admin_notes}
              onChange={(e) => setForm({ ...form, admin_notes: e.target.value })}
              rows={3}
              placeholder="Lighthouse trucks, research progress, outreach notes…"
              data-testid="city-modal-notes"
            />
          </div>
        </div>

        <div className="flex gap-2 pt-2 items-center">
          <Button
            onClick={handleSubmit}
            disabled={saving || !form.slug || !form.name}
            className="flex-1"
            data-testid="city-modal-save"
          >
            <FloppyDisk weight="bold" className="h-4 w-4 mr-1.5" />
            {saving ? "Saving…" : (isCreate ? "Add city" : "Save changes")}
          </Button>
          {!isCreate && !isDefault && onDelete && (
            <Button type="button" variant="destructive" onClick={onDelete} data-testid="city-modal-delete">
              Delete
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose} data-testid="city-modal-cancel">Cancel</Button>
        </div>
        {!isCreate && isDefault && (
          <p className="text-[10px] text-muted-foreground italic">
            This is a default-planned city — editable but not deletable.
          </p>
        )}
      </div>
    </div>
  );
}


/**
 * FoundingSlotsPanel — lists every truck holding a Founding Pro slot in
 * a given city (granted or reserved) with a one-click release button.
 *
 * Trigger: admin does a trial claim on production which eats a slot →
 * they open this panel → find the row → click Release → slot returns to
 * `available`, user's is_founding_pro/is_pro is cleared (if this was
 * their only founding city and they aren't a paying Pro).
 */
function FoundingSlotsPanel({ citySlug, cityLabel, onReleased }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busySlug, setBusySlug] = useState(null);
  const [grantEmail, setGrantEmail] = useState("");
  const [granting, setGranting] = useState(false);
  const [lifetimeOverride, setLifetimeOverride] = useState(false);
  const [revokeEmail, setRevokeEmail] = useState("");
  const [revoking, setRevoking] = useState(false);

  const handleRevoke = async () => {
    const email = revokeEmail.trim();
    if (!email) return;
    if (!window.confirm(`Revoke the ${cityLabel} Founding Pro slot from ${email}?\n\nThe slot returns to the pool and the owner loses Founding Pro for this city.`)) return;
    setRevoking(true);
    try {
      const { data: res } = await api.post("/admin/founding-pro/revoke", { email, city_slug: citySlug });
      toast.success(`Revoked ${cityLabel} slot from ${email}${res.still_pro ? " · owner keeps Pro via another source" : ""}`);
      setRevokeEmail("");
      await load();
      onReleased?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Revoke failed");
    } finally {
      setRevoking(false);
    }
  };

  const handleGrant = async () => {
    const email = grantEmail.trim();
    if (!email) return;
    if (!window.confirm(`Grant a ${cityLabel} Founding Pro slot to ${email}${lifetimeOverride ? " (LIFETIME override)" : ""}?\n\nThis consumes one available slot and cannot be undone from here (use Release on the roster to revoke).`)) return;
    setGranting(true);
    try {
      const { data: res } = await api.post("/admin/founding-pro/grant", { email, city_slug: citySlug, lifetime_override: lifetimeOverride });
      toast.success(`Granted slot #${res.slot_number} to ${email} · ${res.remaining} left${res.expires_at ? ` · through ${res.expires_at.slice(0, 10)}` : " · lifetime"}`);
      setGrantEmail("");
      setLifetimeOverride(false);
      await load();
      onReleased?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Grant failed");
    } finally {
      setGranting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/admin/expansion-cities/${citySlug}/founding-slots`);
      setData(d);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load Founding Pro roster");
    } finally {
      setLoading(false);
    }
  }, [citySlug]);

  useEffect(() => { load(); }, [load]);

  const handleRelease = async (row) => {
    const label = row.truck_name || row.slug;
    const verb = row.slot_status === "granted" ? "release the granted slot for" : "release the reservation for";
    if (!window.confirm(`${verb} ${label}?\n\nThis returns the slot to the available pool. ${row.slot_status === "granted" ? "The owner's Founding Pro status will be revoked (unless they hold slots in other cities).": ""}`)) {
      return;
    }
    setBusySlug(row.slug);
    try {
      const { data: res } = await api.post(
        `/admin/expansion-cities/${citySlug}/founding-slots/${row.slug}/release`,
      );
      toast.success(`Released ${label}${res.user_updated ? " · owner status updated" : ""}`);
      await load();
      onReleased?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Release failed");
    } finally {
      setBusySlug(null);
    }
  };

  const handleReconcile = async () => {
    if (!window.confirm(
      "Reconcile the granted / reserved counters from actual DB state?\n\n" +
      "This is safe — it re-counts real slot-holders and re-syncs the pool. " +
      "Use when the roster below is empty but the counters say slots are consumed."
    )) return;
    try {
      const { data: res } = await api.post(
        `/admin/expansion-cities/${citySlug}/founding-slots/reconcile`,
      );
      const changed = res.before.granted !== res.after.granted || res.before.reserved !== res.after.reserved;
      if (changed) {
        toast.success(
          `Counters synced · granted ${res.before.granted}→${res.after.granted}, reserved ${res.before.reserved}→${res.after.reserved}`,
        );
      } else {
        toast.info("Counters already match reality — nothing to reconcile.");
      }
      await load();
      onReleased?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reconcile failed");
    }
  };

  if (loading) {
    return <div className="text-xs text-muted-foreground">Loading roster…</div>;
  }
  if (!data) return null;

  const emptyGranted = !data.slots.some((s) => s.slot_status === "granted");
  const emptyReserved = !data.slots.some((s) => s.slot_status === "reserved");
  const hasCounterDrift = data.slots.length === 0 && (data.granted > 0 || data.reserved > 0);

  return (
    <div className="rounded-2xl border border-border bg-card p-4 space-y-4" data-testid="founding-slots-panel">
      <div className="grid grid-cols-4 gap-3 text-center text-xs">
        <SlotMeter label="Total" value={data.total} />
        <SlotMeter label="Granted" value={data.granted} tone="emerald" />
        <SlotMeter label="Reserved" value={data.reserved} tone="amber" />
        <SlotMeter label="Available" value={data.available} tone={data.available > 0 ? "primary" : "muted"} />
      </div>

      {hasCounterDrift && (
        <div
          className="rounded-lg border border-rose-300 bg-rose-50 dark:bg-rose-950/20 p-3 text-xs"
          data-testid="founding-slots-drift-warning"
        >
          <div className="font-semibold text-rose-800 dark:text-rose-200 mb-1">
            Counters look drifted
          </div>
          <p className="text-rose-700 dark:text-rose-300 leading-snug">
            The pool says slots are consumed but no truck actually holds one. This usually means
            a test claim or manual DB cleanup skipped the counter update.
            Click reconcile to sync counters with reality.
          </p>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleReconcile}
          data-testid="founding-slots-reconcile"
        >
          Reconcile counters
        </Button>
      </div>

      {/* Grant a slot to an existing owner (signed up before the promo,
          or organic signups you want to reward manually). */}
      <div className="rounded-lg border border-border p-3 space-y-2" data-testid="founding-grant-form">
        <div className="text-xs font-semibold">Grant a slot to an existing owner</div>
        <p className="text-[11px] text-muted-foreground leading-snug">
          Consumes one available slot and gives that owner Founding Pro for this city&apos;s promo term
          (OBX = lifetime, newer markets = 1 year). Use this instead of the plain Pro toggle — the
          toggle does <em>not</em> deduct from the pool.
        </p>
        <div className="flex gap-2">
          <input
            type="email"
            value={grantEmail}
            onChange={(e) => setGrantEmail(e.target.value)}
            placeholder="owner@email.com"
            className="flex-1 h-9 rounded-md border border-border bg-background px-3 text-sm"
            data-testid="founding-grant-email"
          />
          <Button
            type="button"
            size="sm"
            disabled={granting || !grantEmail.trim() || data.available <= 0}
            onClick={handleGrant}
            data-testid="founding-grant-submit"
          >
            {granting ? "Granting…" : "Grant slot"}
          </Button>
        </div>
        <label className="flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={lifetimeOverride}
            onChange={(e) => setLifetimeOverride(e.target.checked)}
            data-testid="founding-grant-lifetime"
          />
          Lifetime override (ignore the city&apos;s 1-year term — e.g. first-truck goodwill)
        </label>
        {data.available <= 0 && (
          <p className="text-[11px] text-rose-600">No slots left in this city.</p>
        )}
      </div>

      {/* Revoke a signup-granted slot by email — for accounts with no
          preloaded-truck row (the per-truck Release button can't reach
          those, e.g. the Meta reviewer test account, Oct 2026). */}
      <div className="rounded-lg border border-border p-3 space-y-2" data-testid="founding-revoke-form">
        <div className="text-xs font-semibold">Revoke a slot by owner email</div>
        <p className="text-[11px] text-muted-foreground leading-snug">
          For slots granted at signup or via the form above (owners without a preloaded-truck row).
          Returns the slot to the pool and clears the owner&apos;s Founding Pro for this city.
        </p>
        <div className="flex gap-2">
          <input
            type="email"
            value={revokeEmail}
            onChange={(e) => setRevokeEmail(e.target.value)}
            placeholder="owner@email.com"
            className="flex-1 h-9 rounded-md border border-border bg-background px-3 text-sm"
            data-testid="founding-revoke-email"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="text-destructive border-destructive/40 hover:bg-destructive/10"
            disabled={revoking || !revokeEmail.trim()}
            onClick={handleRevoke}
            data-testid="founding-revoke-submit"
          >
            {revoking ? "Revoking…" : "Revoke slot"}
          </Button>
        </div>
      </div>

      <SlotBucket
        title="Granted"
        emptyMsg="No granted slots yet in this city."
        empty={emptyGranted}
        rows={data.slots.filter((s) => s.slot_status === "granted")}
        onRelease={handleRelease}
        busySlug={busySlug}
        tone="emerald"
        cityLabel={cityLabel}
      />
      <SlotBucket
        title="Reserved (pending review)"
        emptyMsg="No pending reservations."
        empty={emptyReserved}
        rows={data.slots.filter((s) => s.slot_status === "reserved")}
        onRelease={handleRelease}
        busySlug={busySlug}
        tone="amber"
        cityLabel={cityLabel}
      />
    </div>
  );
}

function SlotMeter({ label, value, tone }) {
  const cls = {
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    primary: "text-primary",
    muted: "text-muted-foreground",
  }[tone] || "text-foreground";
  return (
    <div className="rounded-lg bg-background/60 border border-border py-2">
      <div className={`text-2xl font-black ${cls}`} data-testid={`founding-meter-${label.toLowerCase()}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
    </div>
  );
}

function SlotBucket({ title, empty, emptyMsg, rows, onRelease, busySlug, tone, cityLabel }) {
  const stripe = tone === "emerald" ? "border-emerald-300 bg-emerald-50/40" : "border-amber-300 bg-amber-50/40";
  return (
    <div>
      <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">{title}</h4>
      {empty ? (
        <div className="text-xs text-muted-foreground italic border border-dashed border-border rounded-lg p-3">
          {emptyMsg}
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li
              key={r.slug || r.claimed_by_user_id}
              className={`rounded-lg border ${stripe} p-3 flex items-center justify-between gap-3`}
              data-testid={`founding-slot-row-${r.slug || r.claimed_by_user_id}`}
            >
              <div className="min-w-0 flex-1">
                {r.slug ? (
                  <Link
                    to={`/food-trucks/${r.city_slug}/${r.slug}`}
                    className="text-sm font-semibold hover:underline block truncate"
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`founding-slot-link-${r.slug}`}
                  >
                    {r.truck_name || r.slug}
                    {r.slot_number ? (
                      <span className="ml-2 text-[10px] font-mono text-muted-foreground">#{r.slot_number}</span>
                    ) : null}
                  </Link>
                ) : (
                  <span className="text-sm font-semibold block truncate" data-testid={`founding-slot-signup-${r.claimed_by_user_id}`}>
                    {r.truck_name}
                    {r.slot_number ? (
                      <span className="ml-2 text-[10px] font-mono text-muted-foreground">#{r.slot_number}</span>
                    ) : null}
                  </span>
                )}
                <div className="text-[11px] text-muted-foreground mt-0.5 font-mono truncate">
                  {r.claimed_by_email || <em>no claimant email</em>}
                  <span className="mx-1.5">·</span>
                  {r.claim_status}
                  <span className="mx-1.5">·</span>
                  {cityLabel}
                </div>
              </div>
              {r.slug ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRelease(r)}
                  disabled={busySlug === r.slug}
                  data-testid={`founding-slot-release-${r.slug}`}
                >
                  {busySlug === r.slug ? "Releasing…" : "Release"}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
